
import { Vue, Options, prop } from "vue-class-component";
import { PropType } from "vue";
import {
    CreateBookingV4,
  PaymentMethodBookingV4,
  PaymentMethodOptionV4,
  PaymentMethodV4
} from "@/domain/entities/BookingV4";
import { formatPriceRP, formatPriceRM } from "@/app/infrastructures/misc/Utils";
import { DistrictData } from "@/domain/entities/Location";

class Props {
  paymentMethod = prop<PaymentMethodBookingV4>({
    default: () => new PaymentMethodBookingV4(),
    required: true,
    type: PaymentMethodBookingV4
  });
  paymentMethodOptions = prop<PaymentMethodOptionV4[]>({
    default: () => [],
    required: true,
    type: Array
  });
  currencyPrefix = prop<"Rp" | "RM">({
    default: "Rp",
    required: true,
    type: String as PropType<"Rp" | "RM">
  });
  isReadonly = prop<boolean>({
    default: false,
    type: Boolean
  });
  readonly = prop<boolean>({
    default: false,
    type: Boolean
  });
  form = prop<CreateBookingV4>({
    default: () => new CreateBookingV4(),
    type: Object
  });
}

const TEST_ID_BY_KEY: Record<string, string> = {
  [PaymentMethodV4.CASH]: "txn-booking-radio-payment-cash",
  [PaymentMethodV4.COD]: "txn-booking-radio-payment-cod",
  [PaymentMethodV4.DFOD]: "txn-booking-radio-payment-dfod",
  [PaymentMethodV4.INVOICE_LIMIT]: "txn-booking-radio-payment-pos-limit"
};

const LABEL_KEY_BY_KEY: Record<string, string> = {
  [PaymentMethodV4.CASH]: "cash",
  [PaymentMethodV4.COD]: "cod",
  [PaymentMethodV4.DFOD]: "dfod",
  [PaymentMethodV4.INVOICE_LIMIT]: "invoiceLimit"
};

@Options({
  name: "PaymentMethodSelectorV4",
  emits: ["update:paymentMethod"]
})
class PaymentMethodSelectorV4 extends Vue.with(Props) {
  paymentMethodKeys = PaymentMethodV4;
  hoveredKey: string | null = null;

  testIdFor(key: string) {
    return TEST_ID_BY_KEY[key] || "";
  }

  labelKeyFor(key: string) {
    return LABEL_KEY_BY_KEY[key] || key;
  }

  isHoverableKey(key: string) {
    return (
      key === this.paymentMethodKeys.COD || key === this.paymentMethodKeys.DFOD
    );
  }

  codLogo(option: PaymentMethodOptionV4) {
    return option.disabled
      ? require("@/app/ui/assets/svg/new-cod-disabled.svg")
      : require("@/app/ui/assets/svg/new-cod.svg");
  }

  dfodLogo(option: PaymentMethodOptionV4) {
    return option.disabled
      ? require("@/app/ui/assets/svg/new-dfod-disabled.svg")
      : require("@/app/ui/assets/svg/new-dfod.svg");
  }

  formattedRemainingLimit(remainingLimit: number) {
    return this.currencyPrefix === "RM"
      ? formatPriceRM(remainingLimit)
      : formatPriceRP(remainingLimit);
  }

  get warningOptions() {
    if (this.readonly) return [];
    return this.paymentMethodOptions.filter(
      (option) => this.isHoverableKey(option.key) && option.warningCopy
    );
  }

  onHoverStart(option: PaymentMethodOptionV4) {
    this.hoveredKey = option.key;
  }

  onHoverEnd(option: PaymentMethodOptionV4) {
    if (this.hoveredKey === option.key) {
      this.hoveredKey = null;
    }
  }

  hasHoverText(option: PaymentMethodOptionV4) {
    if (this.isReadonly) return false;
    if (this.readonly) return false;
    if (this.hoveredKey !== option.key) return false;
    if (option.hoverCopy) return true;
    return !option.disabled;
  }

  selectOption(option: PaymentMethodOptionV4) {
    if (option.disabled || this.isReadonly) return;
    if (this.readonly || option.disabled) return;

    this.$emit(
      "update:paymentMethod",
      new PaymentMethodBookingV4({
        ...this.paymentMethod,
        method: option.key
      })
    );
  }

  get isCodDfodDisabled() {
    if (typeof this.form.receiver.destinationCity === "string" && !this.form.receiver.destinationCity) return false;
    // if (this.form.receiver.destinationCity instanceof DistrictData  && this.form.receiver.destinationCity.id) return false;
    if (this.isReadonly) return false;
    if (this.readonly) return false;
    return this.paymentMethodOptions
      .filter((item) => this.isHoverableKey(item.key))
      .some((item) => item.disabled && item.hoverCopy);
  }

  get infobarCopy() {
    return (
      this.paymentMethodOptions
        .filter((item) => this.isHoverableKey(item.key))
        .find((item) => item.disabled)?.hoverCopy || ""
    );
  }
}

export default PaymentMethodSelectorV4;
